import i18n from 'i18next';
import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlarmClock, Check, Download, Paperclip, Plus, Trash2 } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, del, mensagemDeErro, post, getTodos } from '../../nucleo/api';
import { data, dinheiro, hojeIso, textoLivre } from '../../nucleo/formato';
import { COR_SITUACAO, limpar, type DocumentoItem, type Ficha, type Habilidade } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos, textoSensivel } from '../../componentes/sensivel';
import { textoDoNo } from '../../componentes/tabela';

/** Linha rótulo e valor dos cartões da ficha (também em Minha conta > Meus dados). */
export function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-tinta-3">{rotulo}</dt>
      <dd className="min-w-0 truncate text-right" title={textoDoNo(children) || undefined}>
        {anunciarOcultos(children)}
      </dd>
    </div>
  );
}

/** Nível 1 a 5 como pontos preenchidos. */
export function Nivel({ n }: { n: number }) {
  return (
    <span className="inline-flex gap-0.5" role="img" aria-label={`${n}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={`size-2.5 rounded-full border ${i <= n ? 'border-acento-forte bg-acento-forte' : 'border-tinta-3'}`} />
      ))}
    </span>
  );
}

export function SeloSituacao({ d }: { d: Pick<DocumentoItem, 'situacao' | 'dias_para_vencer'> }) {
  const { t } = useTranslation('pessoas');
  const texto =
    d.situacao === 'vencido'
      ? t('situacao.vencidoHa', { n: Math.abs(d.dias_para_vencer ?? 0) })
      : d.situacao === 'vencendo'
        ? t('situacao.venceEm', { n: d.dias_para_vencer ?? 0 })
        : t(`situacao.${d.situacao}`);
  return <Selo cor={COR_SITUACAO[d.situacao]}>{texto}</Selo>;
}

const TIPOS_DOCUMENTO = ['RG', 'CNH', 'CTPS', 'ASO', 'Contrato', 'Comprovante de residência'];
/** Review r29 visual (textos B2): o valor gravado segue em pt; a tela mostra o tipo no idioma (as siglas ficam como estão). */
const CHAVE_TIPO_DOCUMENTO: Record<string, string> = { Contrato: 'contrato', 'Comprovante de residência': 'comprovanteResidencia' };
export function rotuloTipoDocumento(tipo: string, t: (chave: string) => string): string {
  const chave = CHAVE_TIPO_DOCUMENTO[tipo];
  return chave ? t(`pessoas:documentos.tipos.${chave}`) : tipo;
}

/** M7.3: documentos com validade e anexo. */
export function DocumentosCard({ ficha, podeEditar }: { ficha: Ficha; podeEditar: boolean }) {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState(false);
  const [remover, setRemover] = useState<DocumentoItem | null>(null);
  const arquivo = useRef<HTMLInputElement>(null);
  const [nomeArquivo, setNomeArquivo] = useState('');
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pessoas'] });

  const excluir = useMutation({
    mutationFn: (id: string) => del(`/pessoas/documentos/${id}`),
    onSuccess: async () => {
      setRemover(null);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const campos: DefCampo[] = [
    { nome: 'tipo', rotulo: t('campos.tipoDocumento'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: TIPOS_DOCUMENTO.map((x) => ({ valor: x, rotulo: rotuloTipoDocumento(x, t) })) },
    { nome: 'numero', rotulo: t('campos.numero'), tipo: 'texto', largura: 'meia', sensivel: 'oculto' },
    { nome: 'validade', rotulo: t('campos.validade'), tipo: 'data', largura: 'meia', ajuda: t('campos.validadeAjuda') },
  ];

  return (
    <Cartao
      titulo={t('ficha.documentos')}
      acao={
        podeEditar && (
          <Botao variante="fantasma" tamanho="sm" icone={<Plus className="size-4" />} onClick={() => (setNomeArquivo(''), setAberto(true))}>
            {t('acoes.adicionar')}
          </Botao>
        )
      }
    >
      {ficha.documentos.length === 0 ? (
        <p className="text-sm text-tinta-3">{t('ficha.semDocumentos')}</p>
      ) : (
        <ul className="divide-y divide-borda">
          {ficha.documentos.map((d) => (
            <li key={d.id} className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2 text-sm">
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium" title={rotuloTipoDocumento(d.tipo, t)}>{rotuloTipoDocumento(d.tipo, t)}</span>
                <TextoCortado className="numero text-xs text-tinta-3">{[textoSensivel(d.numero), d.validade ? `${t('campos.validade')} ${data(d.validade)}` : null].filter(Boolean).join(' / ')}</TextoCortado>
              </span>
              <span className="flex items-center gap-2">
                <SeloSituacao d={d} />
                {d.anexo_id && (
                  <a href={`/api/v1/anexos/${d.anexo_id}`} target="_blank" rel="noreferrer" aria-label={t('acoes.baixarAnexo')} className="rounded p-1 text-tinta-3 hover:text-tinta-1">
                    <Download className="size-4" />
                  </a>
                )}
                {podeEditar && (
                  <button type="button" aria-label={tn('comum.excluir')} onClick={() => setRemover(d)} className="rounded p-1 text-tinta-3 hover:text-perigo">
                    <Trash2 className="size-4" />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <Modal
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo={t('ficha.novoDocumento')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAberto(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-documento">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <Formulario
          id="form-documento"
          campos={campos}
          inicial={{ tipo: 'RG' }}
          aoEnviar={async (v) => {
            let anexo_id: string | null = null;
            const f = arquivo.current?.files?.[0];
            if (f) {
              const fd = new FormData();
              fd.append('entidade', 'pessoas.funcionario');
              fd.append('registro_id', ficha.id);
              fd.append('arquivo', f);
              anexo_id = (await api<{ id: string }>('POST', '/anexos', fd)).id;
            }
            await post(`/pessoas/funcionarios/${ficha.id}/documentos`, { ...limpar(v), anexo_id });
            setAberto(false);
            setNomeArquivo('');
            await recarregar();
            toast.sucesso(tn('comum.salvo'));
          }}
        >
          <div className="flex flex-col gap-1.5 sm:col-span-6">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-tinta-2">
              <Paperclip className="size-4" aria-hidden />
              {t('campos.anexo')}
            </span>
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <Botao icone={<Paperclip className="size-4" />} onClick={() => arquivo.current?.click()}>
                {nomeArquivo ? t('campos.trocarArquivo') : t('campos.escolherArquivo')}
              </Botao>
              {nomeArquivo && <span className="min-w-0 truncate text-sm text-tinta-2" title={nomeArquivo}>{nomeArquivo}</span>}
              <input ref={arquivo} type="file" className="sr-only" tabIndex={-1} aria-label={t('campos.anexo')} title={t('campos.anexo')} onChange={(e) => setNomeArquivo(e.target.files?.[0]?.name ?? '')} />
            </div>
          </div>
        </Formulario>
      </Modal>
      <Confirmar aberto={!!remover} aoFechar={() => setRemover(null)} aoConfirmar={() => remover && excluir.mutate(remover.id)} titulo={t('confirmar.removerDocumento')} texto={remover ? rotuloTipoDocumento(remover.tipo, t) : ''} perigoso carregando={excluir.isPending} />
    </Cartao>
  );
}

/** M7.5: habilidades da pessoa com nível 1 a 5. */
export function HabilidadesCard({ ficha, podeEditar }: { ficha: Ficha; podeEditar: boolean }) {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState(false);
  const [habilidade, setHabilidade] = useState('');
  const [nivel, setNivel] = useState('3');
  const catalogo = useQuery({ queryKey: ['pessoas', 'habilidades', 'select'], queryFn: () => getTodos<Habilidade>('/pessoas/habilidades'), enabled: aberto });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pessoas'] });
  const definir = useMutation({
    mutationFn: (v: { id: string; nivel: number }) => api('PUT', `/pessoas/funcionarios/${ficha.id}/habilidades/${v.id}`, { nivel: v.nivel }),
    onSuccess: async () => {
      setAberto(false);
      setHabilidade('');
      await recarregar();
      await qc.invalidateQueries({ queryKey: ['chatter'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: (id: string) => del(`/pessoas/funcionarios/${ficha.id}/habilidades/${id}`),
    onSuccess: recarregar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const disponiveis = (catalogo.data?.itens ?? []).filter((h) => !ficha.habilidades.some((x) => x.habilidade_id === h.id));

  return (
    <Cartao
      titulo={t('ficha.habilidades')}
      acao={
        podeEditar && (
          <Botao variante="fantasma" tamanho="sm" icone={<Plus className="size-4" />} onClick={() => setAberto(true)}>
            {t('acoes.adicionar')}
          </Botao>
        )
      }
    >
      {ficha.habilidades.length === 0 ? (
        <p className="text-sm text-tinta-3">{t('ficha.semHabilidades')}</p>
      ) : (
        <ul className="divide-y divide-borda">
          {ficha.habilidades.map((h) => (
            <li key={h.habilidade_id} className="flex min-w-0 items-center justify-between gap-3 py-2 text-sm">
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium" title={h.nome}>{h.nome}</span>
                {h.categoria && <span className="truncate text-xs text-tinta-3" title={h.categoria}>{h.categoria}</span>}
              </span>
              <span className="flex items-center gap-3">
                <Nivel n={h.nivel} />
                {podeEditar && (
                  <button type="button" aria-label={tn('comum.excluir')} onClick={() => remover.mutate(h.habilidade_id)} className="rounded p-1 text-tinta-3 hover:text-perigo">
                    <Trash2 className="size-4" />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <Modal
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo={t('ficha.novaHabilidade')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAberto(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!habilidade} carregando={definir.isPending} onClick={() => definir.mutate({ id: habilidade, nivel: Number(nivel) })}>
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
            {t('campos.habilidade')}
            <Selecao vazio={tn('comum.selecione')} opcoes={disponiveis.map((h) => ({ valor: h.id, rotulo: h.nome }))} value={habilidade} onChange={(e) => setHabilidade(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
            {t('campos.nivel')}
            <Selecao opcoes={[1, 2, 3, 4, 5].map((n) => ({ valor: String(n), rotulo: `${n} - ${t(`nivel.${n}`)}` }))} value={nivel} onChange={(e) => setNivel(e.target.value)} />
          </label>
          {catalogo.isSuccess && disponiveis.length === 0 && <p className="text-sm text-tinta-3">{t('ficha.semHabilidadesNoCatalogo')}</p>}
        </div>
      </Modal>
    </Cartao>
  );
}

/** M7.5: cargo e salário com vigência. */
export function HistoricoCard({ ficha }: { ficha: Ficha }) {
  const { t } = useTranslation('pessoas');
  return (
    <Cartao titulo={t('ficha.historico')}>
      {ficha.historico.length === 0 ? (
        <p className="text-sm text-tinta-3">{t('ficha.semHistorico')}</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {ficha.historico.map((h) => (
            <li key={h.id} className="flex min-w-0 flex-col gap-0.5 border-l-2 border-borda pl-3 text-sm">
              <span className="font-medium">{h.funcao_nome ?? t('ficha.semFuncao')}</span>
              <span className="numero text-xs text-tinta-3">
                {i18n.t('comum.intervalo', { de: data(h.inicio), ate: h.fim ? data(h.fim) : t('ficha.vigente') })}
              </span>
              <span className="numero text-tinta-2">{h.salario_centavos === null ? t('ficha.salarioOculto') : dinheiro(h.salario_centavos, h.moeda)}</span>
              {h.motivo && <span className="text-xs text-tinta-3">{Object.hasOwn(MOTIVOS_DO_SISTEMA, h.motivo) ? t(MOTIVOS_DO_SISTEMA[h.motivo]!) : textoLivre(h.motivo)}</span>}
            </li>
          ))}
        </ol>
      )}
    </Cartao>
  );
}

/** Motivo que o servidor grava em pt (r9 visual B5): aparece no idioma do app; o digitado pelo usuário passa como veio. */
const MOTIVOS_DO_SISTEMA: Record<string, string> = { Admissão: 'ficha.motivoAdmissao' };

/** M7.6: checklist de onboarding da função. */
export function OnboardingCard({ ficha, podeEditar }: { ficha: Ficha; podeEditar: boolean }) {
  const { t } = useTranslation('pessoas');
  const qc = useQueryClient();
  const toast = useToast();
  const marcar = useMutation({
    mutationFn: (v: { id: string; concluido: boolean }) => post(`/pessoas/funcionarios/${ficha.id}/onboarding/${v.id}`, { concluido: v.concluido }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['pessoas'] });
      await qc.invalidateQueries({ queryKey: ['chatter'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const total = ficha.onboarding.length;
  const feitos = ficha.onboarding.filter((i) => i.concluido_em).length;
  const pct = total ? Math.round((feitos / total) * 100) : 0;
  const hoje = hojeIso();

  return (
    <Cartao titulo={t('ficha.onboarding')} acao={total > 0 && <span className="numero text-xs text-tinta-3">{t('ficha.onboardingProgresso', { feitos, total })}</span>}>
      {total === 0 ? (
        <p className="text-sm text-tinta-3">{t('ficha.semOnboarding')}</p>
      ) : (
        <>
          <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-sup-3" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-acento-forte" style={{ width: `${pct}%` }} />
          </div>
          <ul className="divide-y divide-borda">
            {ficha.onboarding.map((i) => {
              const feito = !!i.concluido_em;
              const atrasado = !feito && i.prazo !== null && i.prazo < hoje;
              return (
                <li key={i.item_id} className="flex min-w-0 items-start gap-3 py-2 text-sm">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={feito}
                    aria-label={i.titulo}
                    disabled={!podeEditar || marcar.isPending}
                    onClick={() => marcar.mutate({ id: i.item_id, concluido: !feito })}
                    className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border disabled:opacity-60 ${feito ? 'border-acento-forte bg-acento-forte text-sup-1' : 'border-tinta-3 hover:border-acento-forte'}`}
                  >
                    {feito && <Check className="size-3.5" aria-hidden />}
                  </button>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className={feito ? 'text-tinta-3 line-through' : 'font-medium'}>{i.titulo}</span>
                    {i.descricao && <span className="text-xs text-tinta-3">{textoLivre(i.descricao)}</span>}
                    {feito ? (
                      <span className="text-xs text-tinta-3">{t('ficha.concluidoPor', { nome: i.concluido_por_nome ?? '-', data: data(i.concluido_em) })}</span>
                    ) : (
                      i.prazo && (
                        <span className={`numero inline-flex items-center gap-1 text-xs ${atrasado ? 'text-laranja' : 'text-tinta-3'}`}>
                          {atrasado && <AlarmClock className="size-3.5 shrink-0" aria-hidden />}
                          {t('ficha.prazo', { data: data(i.prazo) })}
                          {atrasado && <span className="font-medium"> {t('ficha.atrasado')}</span>}
                        </span>
                      )
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Cartao>
  );
}
